import React from "react";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link, useInRouterContext } from "react-router-dom";

// Replies come from a model that reads report data, so treat them as untrusted:
// no raw HTML (rehype-raw is not used), no images, and links only to the panel's own origin.

// Returns the in-app path (path + query + hash) for a same-origin http(s) link, else null.
export const toInternalPath = (href: string | undefined): string | null => {
  if (!href) return null;
  try {
    const base = window.location.origin;
    const url = new URL(href, base);
    if ((url.protocol !== "http:" && url.protocol !== "https:") || url.origin !== base) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

const MarkdownLink = ({ href, children }: { href?: string; children?: React.ReactNode }) => {
  const inRouter = useInRouterContext();
  const path = toInternalPath(href);
  // External or unsafe links are shown as plain text.
  if (path === null) return <span>{children}</span>;
  const className = "text-violet-700 underline underline-offset-2 hover:text-violet-900";
  // Use the router so following a link does not reload the page and lose the conversation.
  if (inRouter) {
    return (
      <Link to={path} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={path} className={className}>
      {children}
    </a>
  );
};

const components: Components = {
  img: () => null,
  a: ({ href, children }) => <MarkdownLink href={href}>{children}</MarkdownLink>,
  p: ({ children }) => <p className="my-1.5 first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="my-1.5 list-disc pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-1.5 list-decimal pl-5">{children}</ol>,
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border border-slate-200 bg-slate-100 px-2 py-1 text-left font-semibold">{children}</th>,
  td: ({ children }) => <td className="border border-slate-200 px-2 py-1">{children}</td>,
  pre: ({ children }) => <pre className="my-2 overflow-x-auto rounded bg-slate-100 p-2 text-xs">{children}</pre>,
  code: ({ children }) => <code className="break-words rounded bg-slate-100 px-1 text-xs">{children}</code>,
};

const AssistantMarkdown = ({ text }: { text: string }) => (
  <div className="break-words text-sm leading-relaxed">
    <ReactMarkdown remarkPlugins={[remarkGfm]} disallowedElements={["img"]} components={components}>
      {text}
    </ReactMarkdown>
  </div>
);

export default AssistantMarkdown;
